import { TIMEOUTS } from '@/config/targets'
import { parseSrflxCandidate, type SrflxCandidate } from './candidates'

/**
 * Asks the given STUN servers what address they see us from, over UDP.
 *
 * The data channel exists only to give ICE a reason to gather candidates; nothing is ever sent
 * over it. Returns every server-reflexive candidate that arrived before gathering finished or the
 * timeout ran out — an empty array means no STUN server answered.
 */
export async function gatherSrflxCandidates(
	stunUrls: string[],
	timeoutMs: number = TIMEOUTS.stun
): Promise<SrflxCandidate[]> {
	if (typeof RTCPeerConnection === 'undefined') return []

	const found: SrflxCandidate[] = []
	// One entry, so the browser asks every server from the same local port. That shared port is
	// what makes differing mapped ports across servers meaningful evidence of a symmetric NAT.
	const pc = new RTCPeerConnection({ iceServers: [{ urls: stunUrls }] })

	try {
		await new Promise<void>((resolve) => {
			const timer = setTimeout(resolve, timeoutMs)
			const finish = () => {
				clearTimeout(timer)
				resolve()
			}

			pc.onicecandidate = (event) => {
				// A null candidate is ICE saying it has nothing left to find.
				if (!event.candidate) return finish()

				const candidate = parseSrflxCandidate(event.candidate.candidate)
				if (candidate) found.push(candidate)
			}

			pc.createDataChannel('fwcheck')
			pc.createOffer()
				.then((offer) => pc.setLocalDescription(offer))
				.catch(finish)
		})
	} finally {
		pc.onicecandidate = null
		pc.close()
	}

	return found
}
